import { useEffect, useState } from 'react'

import { Botao, Cartao } from '@/componentes/ui'
import {
  convidarParaInstalar,
  detectarPlataforma,
  estaInstalado,
  observarConviteDeInstalacao,
  type Plataforma,
} from '@/lib/instalacao'
import { AvisoDesktop, PassosIOS } from './instrucoes'

/**
 * Tela de instalação — etapa 0 do onboarding, e bloqueante.
 *
 * A especificação lista o aceite dos termos como primeiro passo, mas no
 * iOS isso não funciona: o Web Push só existe para o app aberto pela
 * tela de início, e a permissão só pode ser pedida de lá. Pedir antes
 * falha em silêncio e a participante passaria os 28 dias sem um único
 * lembrete — justamente o mecanismo de adesão que o app existe para
 * resolver. Por isso a instalação vem primeiro.
 */
export function PaginaInstalar({ aoConcluir }: { aoConcluir: () => void }) {
  const [plataforma] = useState<Plataforma>(detectarPlataforma)
  const [temConvite, setTemConvite] = useState(false)
  const [instalando, setInstalando] = useState(false)

  useEffect(() => observarConviteDeInstalacao(setTemConvite), [])

  // Se ela voltar já pelo ícone, segue direto.
  useEffect(() => {
    if (estaInstalado()) aoConcluir()
  }, [aoConcluir])

  async function instalar() {
    setInstalando(true)
    const aceitou = await convidarParaInstalar()
    setInstalando(false)
    if (aceitou) aoConcluir()
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 px-5 py-10">
      <header className="text-center">
        <span className="text-5xl" aria-hidden>
          🌺
        </span>
        <h1 className="mt-3 font-titulo text-2xl font-bold">Instale o Projeto Viver</h1>
        <p className="mt-2 text-[15px] leading-relaxed text-[var(--tinta-suave)]">
          Adicione o app à tela do seu celular. É assim que ele te lembra da água, do chá, da
          caminhada e do diário todo dia.
        </p>
      </header>

      {plataforma === 'ios' && <PassosIOS />}
      {plataforma === 'android' && (
        <PassosAndroid temConvite={temConvite} instalando={instalando} aoInstalar={instalar} />
      )}
      {plataforma === 'desktop' && <AvisoDesktop />}

      <Cartao className="bg-dourado/15">
        <p className="text-sm leading-relaxed text-[var(--tinta-suave)]">
          <strong className="font-titulo font-semibold text-[var(--tinta)]">Importante:</strong> os
          lembretes só chegam pelo app instalado. Se você abrir pelo navegador, não vai receber
          nenhuma notificação.
        </p>
      </Cartao>
    </main>
  )
}

function PassosAndroid({
  temConvite,
  instalando,
  aoInstalar,
}: {
  temConvite: boolean
  instalando: boolean
  aoInstalar: () => void
}) {
  return (
    <Cartao>
      <h2 className="mb-3 font-titulo text-base font-semibold">No Android</h2>

      {temConvite ? (
        <>
          <p className="mb-4 text-[15px] leading-snug text-[var(--tinta-suave)]">
            É só tocar no botão abaixo e confirmar.
          </p>
          <Botao tamanho="bloco" onClick={aoInstalar} carregando={instalando}>
            Instalar o app
          </Botao>
        </>
      ) : (
        <ol className="flex flex-col gap-4">
          {[
            'Toque nos três pontinhos, no canto superior direito do Chrome.',
            'Escolha "Instalar app" ou "Adicionar à tela inicial".',
            'Confirme e abra o app pelo ícone Viver.',
          ].map((texto, indice) => (
            <li key={texto} className="flex items-start gap-3">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-coral font-titulo text-sm font-bold text-white">
                {indice + 1}
              </span>
              <span className="text-[15px] leading-snug">{texto}</span>
            </li>
          ))}
        </ol>
      )}
    </Cartao>
  )
}
